import deTranslation from "./resources/de/translation.json";
import enTranslation from "./resources/en/translation.json";
import esTranslation from "./resources/es/translation.json";
import frTranslation from "./resources/fr/translation.json";

/** The languages the apps are translated into; the first is the default. */
export const SUPPORTED_LANGUAGES = ["en", "de", "fr", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

export type TranslationDictionary = Record<string, unknown>;

/**
 * Older keys live under `boards` and newer ones under `board`. Each app reads
 * `board.*`, so `board` gains every `boards` key it lacks; `boards` stays for
 * the keys still read from there.
 */
export const withBoardKeyCompatibility = (
  translation: TranslationDictionary,
): TranslationDictionary => {
  const boardSection =
    (translation.board as TranslationDictionary | undefined) ??
    (translation.boards as TranslationDictionary | undefined);
  const boardsSection =
    (translation.boards as TranslationDictionary | undefined) ?? boardSection;
  const mergedBoardSection =
    boardSection && boardsSection
      ? { ...boardsSection, ...boardSection }
      : boardSection ?? boardsSection;

  return {
    ...translation,
    ...(mergedBoardSection ? { board: mergedBoardSection } : {}),
    ...(boardsSection ? { boards: boardsSection } : {}),
  };
};

/**
 * Every language's strings, in the shape i18next takes as `resources`. The web
 * and mobile apps both read these, so a translation lands in both at once.
 */
export const translationResources: Record<
  SupportedLanguage,
  { translation: TranslationDictionary }
> = {
  en: { translation: withBoardKeyCompatibility(enTranslation as TranslationDictionary) },
  de: { translation: withBoardKeyCompatibility(deTranslation as TranslationDictionary) },
  fr: { translation: withBoardKeyCompatibility(frTranslation as TranslationDictionary) },
  es: { translation: withBoardKeyCompatibility(esTranslation as TranslationDictionary) },
};

export const isSupportedLanguage = (
  language: string | null | undefined,
): language is SupportedLanguage =>
  !!language && (SUPPORTED_LANGUAGES as readonly string[]).includes(language);

/**
 * The language to start in: the one the user picked, if it is still
 * supported, else the device's or browser's language family ("de-AT" is
 * German), else the default. Each app passes in where it keeps those.
 */
export const resolveLanguage = (
  savedLanguage: string | null | undefined,
  deviceLanguage: string | null | undefined,
): SupportedLanguage => {
  if (isSupportedLanguage(savedLanguage)) {
    return savedLanguage;
  }

  const family = deviceLanguage?.toLowerCase().split("-")[0];
  return isSupportedLanguage(family) ? family : DEFAULT_LANGUAGE;
};
